import { useEffect, useState } from 'react';

import {
  AppBar,
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  createTheme,
  CssBaseline,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
// --- MUI Icons ---
// Make sure you have installed the @mui/icons-material package
// Run: npm install @mui/icons-material
import MenuIcon from '@mui/icons-material/Menu';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { MdOutlineHub } from 'react-icons/md';

import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // Placeholder for user avatar
import { useLocation, useNavigate } from 'react-router-dom';
import removeTokens from '../../components/Utilities/handleLogout';
import { resetUser } from '../../redux/userReducer';

// --- Animation ---
import { AnimatePresence, motion } from 'framer-motion';
import correct_logo from '../../components/Assets/TigerLogo-new.png';

// --- Theme Definition ---
// Define the theme with the desired color palette
const theme = createTheme({
  palette: {
    mode: 'dark', // Use dark mode for the background
    primary: {
      main: '#FFA500', // Orange color for accents (like the logo circle)
    },
    background: {
      default: '#212121', // Dark grey background similar to the image
      paper: '#FFFFFF', // White background for cards
    },
    text: {
      primary: '#FFFFFF', // White text on dark background
      secondary: '#BDBDBD', // Lighter grey text
      // Define text color for elements on 'paper' background (cards)
      onPaper: '#000000', // Black text on white cards
    },
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif', // Use Inter font
    h3: {
      fontWeight: 'bold',
    },
    h6: {
      color: '#BDBDBD', // Lighter grey for subtitle
    },
    // Define custom typography variants for card content for better control
    cardTitle: {
      fontWeight: 'bold',
      color: '#000000', // Black title on cards
      fontSize: '1.1rem',
      marginBottom: '8px', // Use theme spacing if preferred: theme.spacing(1)
    },
    cardBody: {
      color: '#616161', // Darker grey text on cards
      fontSize: '0.9rem',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for cards
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', // Subtle shadow
          textAlign: 'center',
          height: '100%', // Make cards fill grid item height
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // Center content vertically
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 100, // Avatar size
          height: 100,
          border: '3px solid #FFA500', // Orange border like the image
        },
      },
    },
  },
});

// --- Animation Variants (Framer Motion) ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // Stagger animation of children elements
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 }, // Start slightly lower and faded out
  visible: {
    y: 0, // Animate to original position
    opacity: 1, // Fade in
    transition: {
      type: 'spring', // Use spring animation for a bouncier effect
      stiffness: 100,
    },
  },
};

// Hover effect for cards
const cardHoverEffect = {
  scale: 1.05, // Slightly enlarge card on hover
  boxShadow: '0px 10px 20px rgba(0,0,0,0.2)', // Increase shadow for depth
  transition: { duration: 0.3 }, // Smooth transition for hover effect
};

// --- Main App Component ---
function LandingPage() {
  // Data for the tool cards
  const greetings = [
    'Hi',
    'Hola',
    'Bonjour',
    'Hallo',
    'Ciao',
    '你好',
    'Привет',
    'नमस्ते',
  ];
  const [currentGreetingIndex, setCurrentGreetingIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.PowerBi.user);
  const fullName = [
    user?.FIRSTNAME?.split('.')[0]
      ?.toLowerCase()
      ?.replace(/^\w/, (c) => c.toUpperCase()), // Capitalize first letter
  ];
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    removeTokens();
    dispatch(resetUser({}));
    navigate('/', { state: { location } });
    handleMenuClose(); // Close the menu after logout
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentGreetingIndex(
        (prevIndex) => (prevIndex + 1) % greetings.length,
      );
    }, 3000); // Change every 2 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, []); // Empty dependency array means run once on mount

  const cardTapEffect = {
    scale: 0.9, // Shrink to 90% size on tap/click
    transition: { duration: 0.15, ease: 'easeOut' }, // Optional: customize tap transition (usually faster)
  };

  const tools = [
    {
      icon: <QueryStatsIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Insight Center',
      description:
        'Actionable Business Intelligence for Strategic Decision-Making',
      route: '/reports',
    },
    {
      icon: <MdOutlineHub style={{ fontSize: 50, color: '#FFA500' }} />,
      title: 'Digital Hub',
      description: 'Transforming Processes Through Scalable Digital Solutions',
      route: '/digital-hub',
    },
    // {
    //   icon: <SpeedIcon sx={{fontSize: 50, color: 'primary.main'}} />,
    //   title: 'US+ PMS Pulse',
    //   description: 'Structured Performance Appraisal for US+ Teams',
    //   route: '/pms-page',
    // },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />{' '}
      {/* Ensures background color applies and resets default browser styles */}
      {/* Header Section */}
      <AppBar
        position='static'
        elevation={0} // No shadow for a flatter appearance
        sx={{
          backgroundColor: 'background.default',
          borderBottom: '0.2px solid orange',
        }} // Use the theme's default background color
      >
        <Toolbar>
          {/* Logo Placeholder */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Small orange circle representing the logo accent */}
            <Box
            //   sx={{
            //     width: 24,
            //     height: 24,
            //     backgroundColor: 'primary.main', // Orange circle
            //     borderRadius: '50%',
            //     mr: 1, // Margin right for spacing
            //   }}
            />
            <img
              src={correct_logo}
              alt='Tiger Analytics'
              style={{
                width: 110,
                height: 65,
                // transition: 'opacity 0.3s ease',
                // padding: '4px',
                // borderRadius: '4px',
                // backgroundColor: '#f8fafc',
              }}
            ></img>
            {/* Brand Name */}
          </Box>
          {/* Menu Icon Button */}
          <>
            <IconButton
              size='large'
              edge='end'
              color='inherit'
              aria-label='menu'
              onClick={handleMenuClick}
            >
              <MenuIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                sx={{
                  backgroundColor: 'White',
                  color: 'Orange',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
                onClick={handleLogout}
              >
                Logout
              </MenuItem>
            </Menu>
          </>
        </Toolbar>
      </AppBar>
      {/* Main Content Area */}
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        {' '}
        {/* Add top and bottom margin */}
        {/* Framer Motion container for initial load animation */}
        <motion.div
          initial='hidden' // Start state for animations
          animate='visible' // End state for animations
          variants={containerVariants} // Apply container animation variants
        >
          {/* Greeting Section */}
          {/* Greeting Section */}
          <Grid container spacing={4} alignItems='center' sx={{ mb: 4 }}>
            {/* Greeting Text (Left side now) */}
            <Grid item xs={12} md={8}>
              <motion.div
                variants={itemVariants}
                style={{ flexGrow: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start', // Align to the left now
                    width: '100%',
                  }}
                >
                  <Typography variant='h4' component='div'>
                    <AnimatePresence mode='wait'>
                      <motion.span
                        key={currentGreetingIndex}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.5 }}
                        style={{ display: 'inline-block' }}
                      >
                        {greetings[currentGreetingIndex]}
                      </motion.span>
                    </AnimatePresence>
                  </Typography>
                  <Typography
                    variant='h3'
                    component='h1'
                    sx={{ textAlign: 'left' }}
                  >
                    {fullName}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            {/* Avatar (Right side now) */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' }, // Align right on larger screens
              }}
            >
              <motion.div
                animate={{
                  y: [0, -5, 0],
                  transition: {
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                }}
              >
                <Avatar
                  alt='Picture'
                  src={user?.picture}
                  sx={{ width: 120, height: 120 }}
                >
                  <AccountCircleIcon sx={{ fontSize: 100 }} />
                </Avatar>
              </motion.div>
            </Grid>

            {/* Subtitle (Unchanged) */}
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <motion.div variants={itemVariants}>
                <Typography variant='h6' textAlign='center'>
                  Please select a module to continue
                </Typography>
              </motion.div>
            </Grid>
          </Grid>

          {/* Tool Cards Section */}
          <Grid container spacing={4} justifyContent='center'>
            {' '}
            {/* Center grid items horizontally */}
            {tools.map((tool, index) => (
              // Grid item for each card, responsive sizing
              <Grid item xs={12} sm={6} md={4} key={index}>
                {/* Animate each card individually */}
                <motion.div
                  variants={itemVariants} // Apply item animation variants
                  whileHover={cardHoverEffect} // Apply hover effect
                  whileTap={cardTapEffect} // Apply tap effect
                  style={{ height: '100%' }} // Ensure motion div takes full grid item height for proper card sizing
                >
                  {/* Card Component */}
                  <Card
                    onClick={() => navigate(tool.route)}
                    sx={{ backgroundColor: 'background.paper', height: '100%' }}
                  >
                    {' '}
                    {/* Use theme paper color, ensure full height */}
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center', // Center content horizontally
                        justifyContent: 'center', // Center content vertically
                        p: 3, // Add padding inside the card
                        height: '100%', // Ensure content takes full height
                      }}
                    >
                      {/* Icon */}
                      <Box mb={2}>{tool.icon}</Box>{' '}
                      {/* Add margin below icon */}
                      {/* Card Title - using custom theme variant */}
                      <Typography variant='cardTitle'>{tool.title}</Typography>
                      {/* Card Description - using custom theme variant */}
                      <Typography variant='cardBody'>
                        {tool.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </ThemeProvider>
  );
}

export default LandingPage;
